<template>
  <transition
    name="fade"
    appear
  >
    <div
      v-if="innerShow"
      :id="id"
      ref="modal"
      class="modal fade"
      :class="[{'show': innerShow}, containerClasses]"
      :style="{'display': innerShow? 'flex': ''}"
      data-backdrop="static"
      tabindex="-1"
      role="dialog"
    >
      <div
        class="modal-dialog"
        :class="dialogClasses"
        role="document"
      >
        <div class="modal-content">
          <div
            v-if="title"
            class="modal-header d-flex align-items-center"
          >
            <base-heading :class="`${!showCloseButton ? 'mr-auto' : ' my-1'}`">
              <slot
                name="header"
                :title="title"
              >
                {{ title }}
              </slot>
            </base-heading>

            <button
              v-if="showCloseButton"
              type="button"
              class="close"
              aria-label="Close"
              @click="close()"
            >
              <base-icon icon="close-x" />
            </button>
          </div>
          <button
            v-if="!title && showCloseButton"
            type="button"
            class="close position-absolute"
            style="right: 0; z-index: 9999"
            data-dismiss="modal"
            aria-label="Close"
            @click="close()"
          >
            <base-icon icon="close-x" />
          </button>
          <div
            class="modal-body"
            :class="bodyClasses"
          >
            <slot>Content Here</slot>
          </div>
          <div
            v-if="hasFooter()"
            class="modal-footer"
            :class="{'modal-footer--expanded': expanded}"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>

import { EventBus } from '@/_utilities/eventBus';

export default {
  name: 'AddClientsModal',
  props: {
    id: {
      type: String,
      default: '',
      required: false,
    },
    title: {
      type: String,
      default: '',
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * The size of the modal
     * modal-sm = 300px | modal-md = 500 max | modal-lg= 800px | modal-xl = 1024px
     * @values modal-xl, modal-md
     */
    size: {
      type: String,
      default: 'modal-lg',
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    /**
     * Paddings medium size (30px)
     */
    paddingMd: {
      type: Boolean,
      default: false,
    },
    /**
     * This prop will center the modal vertically - 40px to match figma
     */
    centered: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerShow: false,
    };
  },
  computed: {
    dialogClasses() {
      const classes = [this.size];

      // Removed because centering should be done by the parent container in containerClasses
      // if (this.centered) {
      //   classes.push('modal--centered');
      // }
      return classes;
    },
    bodyClasses() {
      return {
        'modal-body--expanded': this.expanded,
        'modal-body--padding-md': this.paddingMd,
      };
    },
    containerClasses() {
      return this.centered ? { 'center-content': true } : {};
    },
    keymap() {
      return {
        esc: this.close,
      };
    },
  },
  watch: {
    innerShow(value) {
      if (value) {
        document.querySelector('body').classList.add('modal-open');
      } else {
        document.querySelector('body').classList.remove('modal-open');
      }
    },
    show: {
      handler() {
        if (this.show === true) {
          this.showModal();
        } else {
          this.innerShow = false;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Move tooltip outside component
    document.body.appendChild(this.$el);

    // Close modal before opening a new one
    EventBus.$on('modal-open', () => {
      this.innerShow = false;
    });

    if (this.show === true) {
      this.showModal();
    }
  },
  beforeDestroy() {
    /*
    This removes the bad scroll behavior if the modal component is destroyed before is closed
    Prefers :show over v-if
    */
    document.querySelector('body').classList.remove('modal-open');
  },
  destroyed() {
    /**
     * This hides any element relative to the modal
    */
    if (this.$el) {
      this.$el.remove();
    }
  },
  methods: {
    hasFooter() {
      if (!this.$slots.footer) {
        return false;
      }

      return this.showFooter;
    },
    close() {
      this.innerShow = false;
      this.$emit('closed');
    },
    showModal() {
      EventBus.$emit('modal-open');
      this.$nextTick().then(() => {
        this.innerShow = true;
        this.$emit('shown');
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.close {
  opacity: 1;
  padding: 1.875rem;
}
.center-content{
  align-items: center;
  justify-content: center;
}
.base-icon{
  width: 1rem;
  height: 1rem;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.modal{
  background-color: rgba(black, 0.5);
  z-index: $zindex-modal;
}
.modal-full {
  max-width: calc(100vw - 2rem);
}
.modal-xl {
  max-width: 1024px;
}
.modal-lg {
  max-width: 743px;
}
.modal-md {
  max-width: 500px;
}
.modal-header {
  background-color: $gray-light;
  border: none !important;
}
.modal-body--expanded {
  padding: 4.875rem 1.85rem 0 1.85rem;
}
.modal-body--padding-md {
  padding: 1.875rem;
  padding-bottom: 0;
}
.modal-footer--expanded {
  padding: 1.875rem;
}
.modal--centered {
  top: calc(50% - 40px);
  transform: translateY(-50%);
}
.modal-dialog {
  transition: transform 0.3s ease-out, -webkit-transform 0.3s ease-out, max-width 0.2s;
}
@media (max-width: map-get($breakpoints,'lg')) {
  .modal-lg {
    max-width: 728px !important;
    margin:  auto;
  }
 }
</style>
